// rtuSlice.ts:

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { LatLngBoundsExpression } from 'leaflet';
import { PayloadAction } from '@reduxjs/toolkit';
import { RTU } from '../../types/RTU';
import { RTUData } from '../../types/RtuData';
import apiClient from '../../services/apiClient';
import { withRtuProducerFieldDefaults } from '../../utils/rtuDefaults';
import { resolveRtuSerialNo } from '../../utils/rtuIdentity';
import { createKeyedInFlightShare } from '../../utils/shareInFlightRequest';
import { SESSION_SUPERSEDED_MESSAGE, getSessionEpoch, isSessionSupersededError } from '../../services/sessionIdentity';
import { mergeCanonicalStatusFields, withCanonicalStatusCompatibility, withCanonicalStatusCompatibilityForDetail } from '../../utils/statusConfig';

const rtuAllInFlight = createKeyedInFlightShare<RTU[]>();
const RTU_ALL_IN_FLIGHT_KEY = 'GET /rtu/all';
// Keyed per session epoch so a new identity never joins the previous identity's request.
const rtuAllShareKey = () => `${RTU_ALL_IN_FLIGHT_KEY}:${getSessionEpoch()}`;

export const resetRtuAllInFlightSharing = () => {
  rtuAllInFlight.reset();
};

const normalizeListRtu = (rtu: RTU) =>
  withCanonicalStatusCompatibility(withRtuProducerFieldDefaults({
    ...rtu,
    serialNo: resolveRtuSerialNo(rtu),
  }));

const normalizeDetailRtu = (rtu: RTUData) =>
  withCanonicalStatusCompatibilityForDetail({
    ...rtu,
    serialNo: resolveRtuSerialNo(rtu),
  });

interface RTUState {
  loading: boolean;
  rtus: RTU[];
  RTUsdata: RTUData | null; 
  error: string | undefined;
  status: 'idle' | 'loading' | 'failed';
}

const initialState: RTUState = {
  loading: false,
  rtus: [],
  RTUsdata: null,  
  error: undefined,
  status: 'idle',
};


// export const fetchRTUs = createAsyncThunk<RTU[], { bounds?: LatLngBoundsExpression; query?: string }, { rejectValue: string }>(
//   'rtu/fetchRTUs',
//   async (params, thunkAPI) => {
//     try {
//       // Update the URL to match your Spring Boot endpoint
//       const response = await fetch(process.env.REACT_APP_AUTH_API_URL + '/rtu/all');
//       if (!response.ok) {
//         throw new Error('Failed to fetch RTUs');
//       }


//       // If the response is automatically decompressed by the browser
//       const jsonData = await response.json();
//       return jsonData;

//     } catch (error) {
//       return thunkAPI.rejectWithValue((error as Error).message);
//     }
//   }
// );

export const fetchRTUs = createAsyncThunk<
  RTU[],
  { bounds?: LatLngBoundsExpression; query?: string },
  { rejectValue: string }
>('rtu/fetchRTUs', async (_params, thunkAPI) => {
  try {
    // In-flight sharing only: Dashboard mount and WebSocket onConnect often
    // dispatch fetchRTUs in the same window. After this GET settles, the next
    // dispatch (explicit refresh, ping reconciliation, WS reconnect) issues a
    // new request rather than reusing a cached body.
    return await rtuAllInFlight.run(rtuAllShareKey(), async () => {
      const response = await apiClient.get('/rtu/all');
      return (response.data as RTU[]).map(normalizeListRtu);
    });
  } catch (error) {
    return thunkAPI.rejectWithValue(
      isSessionSupersededError(error) ? SESSION_SUPERSEDED_MESSAGE : (error as Error).message
    );
  }
});


// export const fetchRTUData = createAsyncThunk<RTUData, string, { rejectValue: string }>(
//   'rtu/fetchRTUData',
//   async (rtuId, thunkAPI) => {
//     try {
//       const response = await fetch(process.env.REACT_APP_AUTH_API_URL+"/rtu/"+rtuId);
//       if (!response.ok) {
//         throw new Error('Failed to fetch RTU data');
//       }
//       const jsonData: RTUData = await response.json();
//       return jsonData;
//     } catch (error) {
//       return thunkAPI.rejectWithValue((error as Error).message);
//   }
// }
// );

export const fetchRTUData = createAsyncThunk<
  RTUData,
  string,
  { rejectValue: string }
>('rtu/fetchRTUData', async (rtuId, thunkAPI) => {
  try {
    const response = await apiClient.get(`/rtu/${rtuId}`); // Use apiClient instead of fetch
    return normalizeDetailRtu(response.data as RTUData);
  } catch (error) {
    return thunkAPI.rejectWithValue(
      isSessionSupersededError(error) ? SESSION_SUPERSEDED_MESSAGE : (error as Error).message
    );
  }
});


const rtuSlice = createSlice({
  name: 'rtu',
  initialState,
  reducers: {
    updateRtuData(state, action: PayloadAction<RTU>) {
      const index = state.rtus.findIndex(rtu => rtu.id === action.payload.id);
      if (index !== -1) {
        const previous = state.rtus[index];
        const incomingSerial = resolveRtuSerialNo(action.payload);
        const normalized = normalizeListRtu({
          ...previous,
          ...action.payload,
          // Realtime payloads can be narrower than the fleet DTO. Do not
          // erase a known stable identity when the update omits it.
          serialNo: incomingSerial ?? previous.serialNo,
        });
        // Bug 3154: a WebSocket update must never regress a newer canonical
        // status already held in state (e.g. a stale/coalesced message
        // arriving after a fresher one).
        state.rtus[index] = mergeCanonicalStatusFields(previous, normalized);
      } else {
        // If the RTU does not exist, you can choose to add it to the array or handle it as needed
        console.log(`Received data for unknown RTU with ID: ${action.payload.id}`);
      }
    },
    resetRTUData(state) {
      state.RTUsdata = null; // Adjust according to your initial state structure
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRTUs.pending, (state) => {
        state.status = 'loading';
        state.loading = true; 
      })
      .addCase(fetchRTUs.fulfilled, (state, action) => {
        state.status = 'idle';
        // Bug 3154: a list refresh completing after a newer WebSocket status
        // update must not revert that row to an older snapshot.
        state.rtus = action.payload.map((incoming) => {
          const previous = state.rtus.find((rtu) => rtu.id === incoming.id);
          return mergeCanonicalStatusFields(previous, incoming);
        });
        state.loading = false;
        state.error = undefined;
      })
      .addCase(fetchRTUs.rejected, (state, action) => {
        // Answer for an identity that has since changed: nothing to show or report.
        if (action.payload === SESSION_SUPERSEDED_MESSAGE) return;
        state.status = 'failed';
        state.loading = false; 
        state.error = (action.payload as string | undefined) ?? action.error.message;
      });
      builder
      .addCase(fetchRTUData.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(fetchRTUData.fulfilled, (state, action) => {
        state.status = 'idle';
        // Bug 3154: a detail refresh completing after a newer WebSocket
        // status update must not revert the open RTU to an older snapshot.
        state.RTUsdata = state.RTUsdata && state.RTUsdata.id === action.payload.id
          ? mergeCanonicalStatusFields(state.RTUsdata, action.payload)
          : action.payload;
        state.loading = false;
        state.error = undefined;
      })
      .addCase(fetchRTUData.rejected, (state, action) => {
        if (action.payload === SESSION_SUPERSEDED_MESSAGE) return;
        state.status = 'failed';
        // Bugs 3156/3157/3161: a background refresh failure (e.g. a
        // transient network blip while an Actions dialog is open on this
        // RTU) must never destroy already-loaded detail data for the same
        // RTU. Only clear when there was nothing usable for this id already
        // - a genuinely failed initial load, or a failure that belongs to a
        // different id (e.g. mid-navigation to another RTU).
        const requestedId = action.meta.arg;
        if (!state.RTUsdata || String(state.RTUsdata.id) !== String(requestedId)) {
          state.RTUsdata = null;
        }
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { updateRtuData, resetRTUData } = rtuSlice.actions;

export default rtuSlice.reducer;
